import { useEffect, useState } from "react";
import {
  Plug,
  ArrowsClockwise,
  CheckCircle,
  WarningCircle,
  Trash,
  Copy,
  Cpu,
  Plus,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { integrationsApi, fleetApi } from "@/lib/api";

/**
 * Fleet integrations tab — Samsara cloud telematics + white-label hardware ingest.
 * Admin-only. Lives inside SettingsDialog.
 */
export default function IntegrationsTab() {
  return (
    <div className="space-y-6">
      <SamsaraCard />
      <PeopleNetCard />
      <DevicesCard />
    </div>
  );
}

// ---------------------------------------------------------------- Samsara

function SamsaraCard() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [autoCreate, setAutoCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => integrationsApi.samsaraStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!token.trim() || token.length < 10) return toast.error("Paste your Samsara API token");
    setBusy(true);
    try {
      const res = await integrationsApi.samsaraConnect({ api_token: token.trim(), auto_create_unknown: autoCreate });
      toast.success(`Connected — ${res.preview?.total_in_page ?? 0} vehicle(s) detected`);
      setToken("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await integrationsApi.samsaraSync();
      if (r.ok) {
        toast.success(`Synced — ${r.matched} matched, ${r.created} new`);
      } else {
        toast.error(r.error || "Sync failed");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Samsara? Existing assets stay; we just stop pulling locations.")) return;
    setBusy(true);
    try {
      await integrationsApi.samsaraDisconnect();
      toast.success("Samsara disconnected");
      load();
    } finally {
      setBusy(false);
    }
  };

  const connected = !!status?.is_connected;

  return (
    <div className="border border-[#2a2a2a] bg-[#0d0d0d]" data-testid="samsara-card">
      <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-[#ff8c00] bg-[#ff8c00]/10 flex items-center justify-center">
            <Plug size={16} className="text-[#ff8c00]" weight="bold" />
          </div>
          <div>
            <div className="font-heading font-black tracking-tight">Samsara</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Cloud fleet telematics</div>
          </div>
        </div>
        {connected ? (
          <span className="px-2 py-1 border border-[#22c55e]/60 bg-[#22c55e]/10 text-[#22c55e] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="samsara-connected-pill">
            <CheckCircle size={11} weight="fill" /> Connected
          </span>
        ) : (
          <span className="px-2 py-1 border border-[#2a2a2a] text-[#71717a] text-[10px] uppercase tracking-[0.2em] font-bold">
            Not connected
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {!connected && (
          <>
            <p className="text-xs text-[#a1a1aa] leading-relaxed">
              Connect your existing Samsara account to pull live vehicle locations every 60s. Get your token at{" "}
              <span className="font-mono text-[#4682b4]">Samsara Cloud → Settings → API Tokens</span>.
              Required scopes: <span className="font-mono">Read Vehicle Locations</span>,{" "}
              <span className="font-mono">Read Vehicles</span>.
            </p>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="samsara_api_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-xs font-mono"
              data-testid="samsara-token-input"
            />
            <label className="flex items-center gap-2 text-xs text-[#a1a1aa] cursor-pointer">
              <input
                type="checkbox"
                checked={autoCreate}
                onChange={(e) => setAutoCreate(e.target.checked)}
                className="accent-[#ff8c00]"
                data-testid="samsara-auto-create"
              />
              Auto-create assets for unknown Samsara vehicles
            </label>
            <button
              onClick={connect}
              disabled={busy || !token.trim()}
              className="bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-50 text-black font-bold px-4 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center gap-2"
              data-testid="samsara-connect-btn"
            >
              {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : <Plug size={12} weight="bold" />}
              {busy ? "Testing…" : "Connect Samsara"}
            </button>
          </>
        )}

        {connected && (
          <>
            <div className="grid grid-cols-2 gap-3 font-mono text-[11px]">
              <Stat label="Token" value={status.token_preview} />
              <Stat label="Auto-create" value={status.auto_create_unknown ? "Yes" : "No"} />
              <Stat label="Last sync" value={status.last_sync_at ? new Date(status.last_sync_at).toLocaleTimeString() : "—"} />
              <Stat label="Vehicles" value={`${status.last_synced_count ?? 0} (${status.last_matched_count ?? 0} matched / ${status.last_created_count ?? 0} new)`} />
            </div>
            {status.last_error && (
              <div className="px-3 py-2 border border-[#ef4444]/40 bg-[#ef4444]/5 text-[11px] text-[#ef4444] flex items-start gap-2" data-testid="samsara-error">
                <WarningCircle size={12} weight="fill" className="mt-0.5" />
                <span className="font-mono">{status.last_error}</span>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={sync}
                disabled={syncing}
                className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 disabled:opacity-50 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
                data-testid="samsara-sync-btn"
              >
                <ArrowsClockwise size={11} weight="bold" className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync Now"}
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                className="border border-[#ef4444]/60 text-[#ef4444] hover:bg-[#ef4444]/10 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
                data-testid="samsara-disconnect-btn"
              >
                <Trash size={11} weight="bold" /> Disconnect
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#2a2a2a] p-2">
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a]">{label}</div>
      <div className="text-white truncate" title={value}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------- PeopleNet / Trimble

function PeopleNetCard() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ endpoint: "", org_id: "", username: "", password: "", auto_create_unknown: true });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => integrationsApi.peoplenetStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!form.endpoint || !form.org_id || !form.username || !form.password) {
      return toast.error("Endpoint, Company ID, username and password are all required");
    }
    setBusy(true);
    try {
      const res = await integrationsApi.peoplenetConnect(form);
      toast.success(`Connected to PFM — ${res.preview?.total_in_page ?? 0} vehicle(s) detected`);
      setForm({ ...form, password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await integrationsApi.peoplenetSync();
      if (r.ok) toast.success(`Synced — ${r.matched} matched, ${r.created} new`);
      else toast.error(r.error || "Sync failed");
      load();
    } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect PeopleNet? Existing assets stay; we just stop pulling positions.")) return;
    setBusy(true);
    try {
      await integrationsApi.peoplenetDisconnect();
      toast.success("PeopleNet disconnected");
      load();
    } finally { setBusy(false); }
  };

  const connected = !!status?.is_connected;

  return (
    <div className="border border-[#2a2a2a] bg-[#0d0d0d]" data-testid="peoplenet-card">
      <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-[#4682b4] bg-[#4682b4]/10 flex items-center justify-center">
            <Plug size={16} className="text-[#4682b4]" weight="bold" />
          </div>
          <div>
            <div className="font-heading font-black tracking-tight">PeopleNet / Trimble PFM</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Enterprise fleet telematics</div>
          </div>
        </div>
        {connected ? (
          <span className="px-2 py-1 border border-[#22c55e]/60 bg-[#22c55e]/10 text-[#22c55e] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="peoplenet-connected-pill">
            <CheckCircle size={11} weight="fill" /> Connected
          </span>
        ) : (
          <span className="px-2 py-1 border border-[#2a2a2a] text-[#71717a] text-[10px] uppercase tracking-[0.2em] font-bold">Not connected</span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {!connected && (
          <>
            <p className="text-xs text-[#a1a1aa] leading-relaxed">
              Trimble Fleet Manager (formerly PeopleNet PFM) doesn't expose a public REST API — your Trimble
              account manager will issue your private endpoint URL. Paste it here along with your{" "}
              <span className="font-mono text-[#4682b4]">Company ID</span>, username and password. We poll{" "}
              <span className="font-mono">/vehicles/positions</span> every 60s.
            </p>
            <input
              type="url"
              value={form.endpoint}
              onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
              placeholder="https://pfm-api.your-tenant.trimble.com"
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#4682b4] outline-none px-3 py-2 text-xs font-mono"
              data-testid="peoplenet-endpoint-input"
            />
            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                value={form.org_id}
                onChange={(e) => setForm({ ...form, org_id: e.target.value })}
                placeholder="Company ID"
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#4682b4] outline-none px-3 py-2 text-xs font-mono"
                data-testid="peoplenet-orgid-input"
              />
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="Username"
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#4682b4] outline-none px-3 py-2 text-xs font-mono"
                data-testid="peoplenet-username-input"
              />
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password"
                className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#4682b4] outline-none px-3 py-2 text-xs font-mono"
                data-testid="peoplenet-password-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#a1a1aa] cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_create_unknown}
                onChange={(e) => setForm({ ...form, auto_create_unknown: e.target.checked })}
                className="accent-[#4682b4]"
                data-testid="peoplenet-auto-create"
              />
              Auto-create assets for unknown PFM vehicles
            </label>
            <button
              onClick={connect}
              disabled={busy}
              className="bg-[#4682b4] hover:bg-[#4682b4]/90 disabled:opacity-50 text-white font-bold px-4 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center gap-2"
              data-testid="peoplenet-connect-btn"
            >
              {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : <Plug size={12} weight="bold" />}
              {busy ? "Testing…" : "Connect PeopleNet"}
            </button>
          </>
        )}

        {connected && (
          <>
            <div className="grid grid-cols-2 gap-3 font-mono text-[11px]">
              <Stat label="Endpoint" value={status.endpoint} />
              <Stat label="Company ID" value={status.org_id} />
              <Stat label="Username" value={status.username} />
              <Stat label="Auto-create" value={status.auto_create_unknown ? "Yes" : "No"} />
              <Stat label="Last sync" value={status.last_sync_at ? new Date(status.last_sync_at).toLocaleTimeString() : "—"} />
              <Stat label="Vehicles" value={`${status.last_synced_count ?? 0} (${status.last_matched_count ?? 0} matched / ${status.last_created_count ?? 0} new)`} />
            </div>
            {status.last_error && (
              <div className="px-3 py-2 border border-[#ef4444]/40 bg-[#ef4444]/5 text-[11px] text-[#ef4444] flex items-start gap-2" data-testid="peoplenet-error">
                <WarningCircle size={12} weight="fill" className="mt-0.5" />
                <span className="font-mono">{status.last_error}</span>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={sync}
                disabled={syncing}
                className="border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4]/10 disabled:opacity-50 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
                data-testid="peoplenet-sync-btn"
              >
                <ArrowsClockwise size={11} weight="bold" className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync Now"}
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                className="border border-[#ef4444]/60 text-[#ef4444] hover:bg-[#ef4444]/10 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2"
                data-testid="peoplenet-disconnect-btn"
              >
                <Trash size={11} weight="bold" /> Disconnect
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------- Devices

function DevicesCard() {
  const [devices, setDevices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showProvision, setShowProvision] = useState(false);
  const [newToken, setNewToken] = useState(null);

  const load = async () => {
    const [d, a] = await Promise.all([
      integrationsApi.listDevices().catch(() => []),
      fleetApi.list().catch(() => []),
    ]);
    setDevices(d);
    setAssets(a);
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this device token? The tracker will stop sending pings until re-provisioned.")) return;
    try {
      await integrationsApi.revokeDevice(id);
      toast.success("Device revoked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="border border-[#2a2a2a] bg-[#0d0d0d]" data-testid="devices-card">
      <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-[#4682b4] bg-[#4682b4]/10 flex items-center justify-center">
            <Cpu size={16} className="text-[#4682b4]" weight="bold" />
          </div>
          <div>
            <div className="font-heading font-black tracking-tight">White-Label Hardware</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
              Teltonika FMC130 · Queclink · ATrack
            </div>
          </div>
        </div>
        <button
          onClick={() => { setShowProvision(true); setNewToken(null); }}
          className="border border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00]/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-1"
          data-testid="provision-device-btn"
        >
          <Plus size={11} weight="bold" /> Provision
        </button>
      </div>

      <div className="p-4 space-y-3">
        {devices.length === 0 && !showProvision && (
          <div className="text-xs text-[#71717a] py-4 text-center font-mono">
            No hardware devices provisioned yet.
          </div>
        )}

        {devices.map((d) => (
          <div key={d.id} className="border border-[#2a2a2a] p-3 flex items-start justify-between gap-3" data-testid={`device-row-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold flex items-center gap-2">
                {d.label}
                {d.last_seen_at ? (
                  <span className="px-1.5 py-0.5 border border-[#22c55e]/60 text-[#22c55e] text-[9px] uppercase tracking-[0.2em] font-bold">Live</span>
                ) : (
                  <span className="px-1.5 py-0.5 border border-[#71717a]/60 text-[#71717a] text-[9px] uppercase tracking-[0.2em] font-bold">Pending</span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] mt-0.5">
                → {d.asset_name} · {d.ping_count} pings
                {d.last_seen_at && <> · last {new Date(d.last_seen_at).toLocaleTimeString()}</>}
              </div>
              <div className="font-mono text-[10px] text-[#71717a] mt-1">{d.token_preview}</div>
            </div>
            <button
              onClick={() => revoke(d.id)}
              className="text-[#ef4444] hover:bg-[#ef4444]/10 p-2"
              data-testid={`device-revoke-${d.id}`}
              title="Revoke"
            >
              <Trash size={12} weight="bold" />
            </button>
          </div>
        ))}

        {newToken && <NewTokenDisplay newToken={newToken} onClose={() => setNewToken(null)} />}

        {showProvision && !newToken && (
          <ProvisionForm
            assets={assets}
            existingAssetIds={devices.map((d) => d.asset_id)}
            onCancel={() => setShowProvision(false)}
            onCreated={(token) => { setNewToken(token); setShowProvision(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function ProvisionForm({ assets, existingAssetIds, onCancel, onCreated }) {
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!assetId) return toast.error("Pick an asset to bind this device to");
    setBusy(true);
    try {
      const res = await integrationsApi.provisionDevice({ asset_id: assetId, label });
      onCreated(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Provision failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#ff8c00]/40 bg-[#ff8c00]/5 p-3 space-y-2" data-testid="provision-form">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Provision new device</div>
      <select
        value={assetId}
        onChange={(e) => setAssetId(e.target.value)}
        className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-xs"
        data-testid="provision-asset-select"
      >
        <option value="">— Select asset —</option>
        {assets.map((a) => (
          <option key={a.id} value={a.id}>
            {`${a.name} (${a.type})${existingAssetIds.includes(a.id) ? " · already has a device" : ""}`}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Device label (e.g. FMC130 #A2-0017)"
        className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-xs"
        data-testid="provision-label-input"
      />
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={busy}
          className="bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-50 text-black font-bold px-3 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center gap-2"
          data-testid="provision-submit-btn"
        >
          {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : <CaretRight size={11} weight="bold" />}
          Generate Token
        </button>
        <button
          onClick={onCancel}
          className="border border-[#2a2a2a] text-[#a1a1aa] hover:text-white px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold"
          data-testid="provision-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function NewTokenDisplay({ newToken, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(newToken.token);
    toast.success("Token copied to clipboard");
  };
  return (
    <div className="border border-[#22c55e]/60 bg-[#22c55e]/5 p-3 space-y-2" data-testid="new-token-display">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#22c55e] font-bold flex items-center gap-1">
        <CheckCircle size={11} weight="fill" /> Device provisioned — copy token now
      </div>
      <p className="text-[11px] text-[#a1a1aa] leading-relaxed">
        This token is shown <strong className="text-white">only once</strong>. Paste it into your tracker firmware,
        SIM config, or the device's web UI. If lost, revoke + reprovision.
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-[#1a1a1a] border border-[#2a2a2a] px-2 py-1.5 text-[11px] font-mono break-all" data-testid="new-token-value">
          {newToken.token}
        </code>
        <button
          onClick={copy}
          className="border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10 p-2"
          data-testid="new-token-copy"
        >
          <Copy size={12} weight="bold" />
        </button>
      </div>
      <div className="text-[10px] text-[#71717a] font-mono leading-relaxed">
        POST → <span className="text-[#4682b4]">{newToken.ingest_url}</span><br/>
        Header → <span className="text-[#4682b4]">Authorization: Bearer &lt;token&gt;</span><br/>
        Body → <span className="text-[#4682b4]">{`{lat, lng, speed, heading, ts}`}</span>
      </div>
      <button
        onClick={onClose}
        className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] hover:text-white font-bold"
        data-testid="new-token-close"
      >
        I've copied it — close
      </button>
    </div>
  );
}
